import { CardTimer } from "@/components/Dashboard/components/Card/Card.Timer";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { Input } from "@/components/Base/Input";
import { minutesToMilliseconds } from "date-fns";
import {
  DEFAULT_TIMER_RUNNING_OVER,
  DEFAULT_TIMER_RUNNING_UNDER,
  DEFAULT_TIMER_VALUE,
} from "@/lib/const";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCompactNotes } from "@/hooks/useCompactNotes";
import { NotesPopoverProps } from "@/components/Dashboard/components/Card/NotesPopover";
import { AttachmentsPopoverProps } from "@/components/Dashboard/components/Card/AttachmentsPopover";
import { ColorPickerProps } from "@/components/Dashboard/components/ColorPicker/ColorPicker";
import { HexColor, Timer } from "@/types/v2";
import { CardStarProps } from "@/components/Dashboard/components/Card/Card.Star";
import { CardActivity } from "@/components/Dashboard/components/Card/Card.Activity";
import {
  JobHourDisplay,
  JobHourDisplayProps,
} from "@/components/Dashboard/components/Card/JobHourDisplay";

export interface CardFooterProps
  extends NotesPopoverProps,
    AttachmentsPopoverProps,
    ColorPickerProps,
    Omit<JobHourDisplayProps, "totalLaborHours">,
    Pick<CardStarProps, "onStarToggle"> {
  isCardNotesEnabled?: boolean;
  isCardAttachmentsEnabled?: boolean;
  isCardColorEnabled?: boolean;
  isCardLaborEnabled?: boolean;
  isTasksTabEnabled?: boolean;
  bgColor: HexColor;
  onTimerLimitChange?: (timeLimit: string) => void;
  onTimerStart?: () => void;
  onTimerStop?: () => void;
  onTimerReset?: () => void;
  onTimerPause?: (accumulatedTime: number) => void;
  defaultTimerValue?: string;
  timerUnits?: "minutes" | "hours";
}
export const CardFooter = ({
  cardData,
  isCardNotesEnabled,
  isCardAttachmentsEnabled,
  isCardColorEnabled,
  isCardLaborEnabled,
  isTasksTabEnabled,
  disabled,
  onColorChange,
  onTimerLimitChange,
  onTimerStart,
  onTimerStop,
  onTimerReset,
  onTimerPause,
  onStarToggle,
  defaultTimerValue = DEFAULT_TIMER_VALUE,
  timerUnits,
  ...props
}: CardFooterProps) => {
  const timerLimit = useMemo(
    () => cardData.timer?.timeLimitInMinutes ?? defaultTimerValue,
    [cardData.timer?.timeLimitInMinutes, defaultTimerValue]
  );

  const [timerLimitInUnits, setTimerLimitInUnits] =
    useState<string>(timerLimit);

  useEffect(() => {
    setTimerLimitInUnits(
      timerUnits === "minutes" ? timerLimit : `${parseFloat(timerLimit) / 60}`
    );
  }, [timerLimit, timerUnits]);

  const [timerColor, setTimerColor] = useState<string>("transparent");

  const handleTimerLimitChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTimerLimitInUnits(e.target.value);
  };

  const handleBlur = () => {
    if (onTimerLimitChange) {
      onTimerLimitChange(
        timerUnits === "minutes"
          ? timerLimitInUnits
          : `${parseFloat(timerLimitInUnits) * 60}`
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  const updateTimerColor = useCallback(
    (time: number, state: Timer["state"]) => {
      if (state === "paused") {
        return setTimerColor("bg-white");
      }
      if (state === "running") {
        if (time > parseFloat(timerLimit) * 60 * 1000) {
          return setTimerColor(DEFAULT_TIMER_RUNNING_OVER);
        }
        return setTimerColor(DEFAULT_TIMER_RUNNING_UNDER);
      }
      return setTimerColor("bg-none");
    },
    [timerLimit]
  );

  const isOverdue = timerColor === DEFAULT_TIMER_RUNNING_OVER;

  const footerRef = useRef<HTMLDivElement>(null);
  // Notes number yields to the timer when that is what keeps the timer unclipped.
  const compactNotes = useCompactNotes(footerRef, [
    cardData.notes?.length ?? 0,
    isCardLaborEnabled,
    isCardNotesEnabled,
    // The timer's text and elements change with its state/colour without the
    // footer resizing; re-fit when they do.
    cardData.timer?.state,
    timerColor,
    // JobHourDisplay unmounts its slot when manual hours are off on a
    // manual-only card; re-fit when either flips.
    props.isManualJobHoursEnabled,
    props.isManualCardOnly,
  ]);

  return (
    <div
      ref={footerRef}
      className={cn(
        "mt-1 flex h-[1.125rem] flex-row items-center gap-0.5 overflow-hidden rounded-b-sm border-t border-[#0000000D] px-0.5 py-px",
        timerColor,
        // Over-limit: red-600 bg from the constant, red-800 top border and white
        // text. The white-forcing override for the timer's Button/svg is NOT on
        // this root any more - see the wrapper below - so the activity button's
        // own state colours (green all-done, neutral active cells) survive.
        isOverdue && "border-[#991B1B] text-white"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        data-hours-group=""
        // Timer, then the hours slot (flex-1 min-w-0, right-aligned, with a
        // min-width of the short form - see JobHourDisplay). The hours are
        // NEVER hidden: when the group is too narrow the timer, which may
        // shrink (min-w-0 overflow-hidden), is clipped instead, and the Notes
        // number yields before that (useCompactNotes). The activity button next
        // to this group never gives up room.
        className="flex h-4 min-w-0 flex-1 flex-row items-center gap-x-0.5 overflow-hidden"
      >
        <div
          // display: contents keeps CardTimer a direct flex item of the group
          // while giving the override a scope that excludes the activity button.
          className={cn(
            "contents",
            isOverdue && "[&_button]:!text-white [&_svg]:!text-white"
          )}
        >
          <CardTimer
            compact
            startTime={firestoreToDateTime(cardData.timer?.timeStarted)}
            limit={minutesToMilliseconds(parseFloat(timerLimit))}
            onStart={onTimerStart}
            onStop={onTimerStop}
            onPause={onTimerPause}
            onReset={onTimerReset}
            state={cardData.timer?.state}
            accumulatedTime={cardData.timer?.accumulatedTime ?? 0}
            stopComponent={
              <Input
                size={3}
                value={timerLimitInUnits}
                className="my-0 h-4 w-auto rounded-s border-none bg-transparent p-0 text-[11px] font-semibold text-[#1e3a8a] focus:bg-white focus:outline-none active:bg-white"
                onChange={handleTimerLimitChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />
            }
            callback={updateTimerColor}
            disabled={disabled}
          />
        </div>
        {isCardLaborEnabled && (
          <JobHourDisplay
            totalLaborHours={cardData.totalLaborHours}
            className="text-[10px]/4"
            compact
            {...props}
          />
        )}
      </div>
      <CardActivity
        cardData={cardData}
        shopId={props.shopId}
        color={props.color}
        disabled={disabled}
        overdue={isOverdue}
        isNotesEnabled={isCardNotesEnabled}
        isTasksEnabled={isTasksTabEnabled}
        isFilesEnabled={isCardAttachmentsEnabled}
        isColorEnabled={isCardColorEnabled}
        compactNotes={compactNotes}
        onColorChange={onColorChange}
        onStarToggle={onStarToggle}
      />
    </div>
  );
};
